import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  ButtonGroup,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Paper,
  Alert,
  CircularProgress,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  ChevronLeft as PrevIcon,
  ChevronRight as NextIcon,
  Today as TodayIcon,
  ViewModule as MonthIcon,
  Refresh as RefreshIcon,
  LocalShipping as TruckIcon,
  Person as PersonIcon,
  Assignment as JobIcon,
  Task as TaskIcon,
  Check as CheckIcon,
  AccessTime as TimeIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon
} from '@mui/icons-material';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './CalendarStyles.css';
import { makeApiCall } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { canViewCustomerPhone } from '../../utils/roles';

const localizer = momentLocalizer(moment);

// TKT-237: legend swatches. Keep these hex values in sync with the backend
// CALENDAR_COLORS map in routes/calendar.js — events are coloured by category
// (job type / tasks / maintenance) so the seasons are distinguishable.
const CALENDAR_LEGEND = [
  { key: 'summer', label: 'Summer', color: '#2e7d32' },
  { key: 'winter', label: 'Winter', color: '#1565c0' },
  { key: 'construction', label: 'Construction', color: '#ef6c00' },
  { key: 'task', label: 'Tasks', color: '#00838f' },
  { key: 'maintenance', label: 'Maintenance', color: '#5d4037' },
];

// Custom styles for different event types
const eventStyleGetter = (event) => {
  return {
    style: {
      backgroundColor: event.backgroundColor || event.color,
      borderLeft: `4px solid ${event.color}`,
      borderRadius: '4px',
      opacity: event.resource?.is_complete ? 0.7 : 1,
      color: '#333',
      border: 'none',
      display: 'block'
    }
  };
};

const CalendarView = ({ onNavigateToJob, initialJobType = '' }) => {
  const { user } = useAuth();
  const showCustomerPhone = canViewCustomerPhone(user);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Calendar state
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('month'); // 'month' or 'agenda'
  const [events, setEvents] = useState([]);
  
  // Filter state. TKT-191: seed the job-type filter from the host when
  // the calendar is opened inside Summer/Winter/Construction Logs so we
  // don't accidentally show every job type on those scoped pages.
  const [viewType, setViewType] = useState('all'); // 'all', 'jobs', 'tasks', 'maintenance'
  const [selectedTruck, setSelectedTruck] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [selectedJobType, setSelectedJobType] = useState(initialJobType || '');
  
  // Modal state
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventDetailsOpen, setEventDetailsOpen] = useState(false);
  const [dayDetailsOpen, setDayDetailsOpen] = useState(false);
  const [selectedDayData, setSelectedDayData] = useState(null);
  
  // Resources
  const [trucks, setTrucks] = useState([]);
  const [employees, setEmployees] = useState([]);
  
  // Statistics
  const [stats, setStats] = useState({});

  // ====================================
  // DATA FETCHING
  // ====================================

  useEffect(() => {
    fetchResources();
  }, []);

  useEffect(() => {
    fetchCalendarData();
    fetchStats();
  }, [currentDate, view, viewType, selectedTruck, selectedEmployee, selectedJobType]);

  const fetchResources = async () => {
    try {
      // TKT-188: Employee filter was blank because (a) the default page
      // size on /api/employees is 10 and (b) the rendered field reached
      // for emp.first_name/last_name, which that endpoint does not
      // return at the top level (it returns employee_name + the linked
      // user_first_name / user_last_name). Ask for the full active list
      // and normalize a display_name on each row up front.
      const [trucksRes, employeesRes] = await Promise.all([
        makeApiCall('/api/routes/trucks'),
        makeApiCall('/api/employees?is_active=true&limit=1000')
      ]);
      const trucksData = await trucksRes.json();
      const employeesData = await employeesRes.json();
      setTrucks(trucksData.trucks || []);
      const rawEmployees = employeesData.employees || [];
      const normalizedEmployees = rawEmployees
        .map((e) => ({
          ...e,
          display_name:
            e.employee_name
            || [e.user_first_name, e.user_last_name].filter(Boolean).join(' ')
            || e.username
            || `Employee #${e.id}`,
        }))
        .sort((a, b) => a.display_name.localeCompare(b.display_name));
      setEmployees(normalizedEmployees);
    } catch (err) {
      console.error('Failed to fetch resources:', err);
    }
  };

  const fetchCalendarData = async () => {
    setLoading(true);
    setError('');
    
    try {
      // Calculate date range based on current view (month or agenda).
      let startDate, endDate;
      if (view === 'month') {
        startDate = moment(currentDate).startOf('month').subtract(7, 'days').format('YYYY-MM-DD');
        endDate = moment(currentDate).endOf('month').add(7, 'days').format('YYYY-MM-DD');
      } else {
        // agenda: rolling 30 days from the current date
        startDate = moment(currentDate).format('YYYY-MM-DD');
        endDate = moment(currentDate).add(30, 'days').format('YYYY-MM-DD');
      }

      const params = new URLSearchParams({
        start_date: startDate,
        end_date: endDate,
        view_type: viewType
      });

      if (selectedTruck) params.append('truck_id', selectedTruck);
      if (selectedEmployee) params.append('employee_id', selectedEmployee);
      if (selectedJobType) params.append('job_type', selectedJobType);

      const response = await makeApiCall(`/api/calendar/events?${params}`);
      const data = await response.json();

      // The backend sends naive ISO strings (no 'Z') so browsers in any
      // timezone render an "8:00 AM" slot as their local 8 AM. new Date()
      // on a string without offset treats it as local time - exactly what
      // we want for a calendar showing business hours.
      const eventsWithDates = (data.events || []).map((event) => ({
        ...event,
        start: new Date(event.start),
        end: new Date(event.end),
      }));

      setEvents(eventsWithDates);
      
    } catch (err) {
      console.error('Failed to fetch calendar data:', err);
      setError('Failed to load calendar data');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const year = moment(currentDate).year();
      const month = moment(currentDate).month() + 1;
      
      const response = await makeApiCall(`/api/calendar/stats?year=${year}&month=${month}`);
      const data = await response.json();
      setStats(data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  const fetchDayDetails = async (date) => {
    try {
      // Ensure we get just the date part, ignoring time and timezone
      const formattedDate = moment(date).startOf('day').format('YYYY-MM-DD');
      console.log('Fetching day details for:', formattedDate);
      const response = await makeApiCall(`/api/calendar/day/${formattedDate}`);
      const data = await response.json();
      console.log('Day details response:', data);
      setSelectedDayData(data);
      setDayDetailsOpen(true);
    } catch (err) {
      console.error('Failed to fetch day details:', err);
      setError('Failed to load day details');
    }
  };

  // ====================================
  // EVENT HANDLERS
  // ====================================

  const handleNavigate = (newDate, newView) => {
    setCurrentDate(newDate);
    if (newView) setView(newView);
  };

  // Helper to get the proper time unit for navigation
  const getNavigationUnit = () => {
    switch (view) {
      case 'month': return 'months';
      case 'agenda': return 'days';
      default: return 'days';
    }
  };

  const handleViewChange = (newView) => {
    setView(newView);
  };

  const handleSelectEvent = (event) => {
    setSelectedEvent(event);
    setEventDetailsOpen(true);
  };

  const handleSelectSlot = ({ start }) => {
    // Open quick create dialog or show day details
    fetchDayDetails(start);
  };

  const handleNavigateToJob = (jobId) => {
    setEventDetailsOpen(false);
    if (onNavigateToJob) {
      onNavigateToJob(jobId);
    }
  };

  // ====================================
  // RENDER FUNCTIONS
  // ====================================

  // TKT-188: support direct month/year jumps so the user can navigate
  // straight to "March 2027" without clicking the chevron 10 times.
  const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const currentMoment = moment(currentDate);
  const currentMonthIdx = currentMoment.month();
  const currentYearVal = currentMoment.year();
  const yearOptions = (() => {
    const nowYear = moment().year();
    const out = [];
    for (let y = nowYear - 3; y <= nowYear + 3; y += 1) out.push(y);
    return out;
  })();

  const handleJumpMonth = (newMonthIdx) => {
    const next = moment(currentDate).month(newMonthIdx).startOf('month').toDate();
    handleNavigate(next);
  };
  const handleJumpYear = (newYear) => {
    const next = moment(currentDate).year(newYear).startOf('month').toDate();
    handleNavigate(next);
  };

  const renderToolbar = () => (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Grid container spacing={2} alignItems="center">
          {/* Navigation Controls */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <IconButton
                onClick={() => handleNavigate(moment(currentDate).subtract(1, getNavigationUnit()).toDate())}
                size="small"
                aria-label="Previous month"
              >
                <PrevIcon />
              </IconButton>
              <Button 
                variant="outlined" 
                onClick={() => handleNavigate(new Date())}
                startIcon={<TodayIcon />}
                size="small"
              >
                Today
              </Button>
              <IconButton
                onClick={() => handleNavigate(moment(currentDate).add(1, getNavigationUnit()).toDate())}
                size="small"
                aria-label="Next month"
              >
                <NextIcon />
              </IconButton>
              {view === 'month' && (
                <>
                  <FormControl size="small" sx={{ minWidth: 130 }}>
                    <InputLabel id="cal-month-label">Month</InputLabel>
                    <Select
                      labelId="cal-month-label"
                      label="Month"
                      value={currentMonthIdx}
                      onChange={(e) => handleJumpMonth(Number(e.target.value))}
                    >
                      {MONTH_NAMES.map((m, idx) => (
                        <MenuItem key={m} value={idx}>{m}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 95 }}>
                    <InputLabel id="cal-year-label">Year</InputLabel>
                    <Select
                      labelId="cal-year-label"
                      label="Year"
                      value={currentYearVal}
                      onChange={(e) => handleJumpYear(Number(e.target.value))}
                    >
                      {yearOptions.map((y) => (
                        <MenuItem key={y} value={y}>{y}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </>
              )}
              <Typography variant="h6" sx={{ ml: 1 }}>
                {view === 'month'
                  ? moment(currentDate).format('MMMM YYYY')
                  : `${moment(currentDate).format('MMM DD')} – ${moment(currentDate).add(30, 'days').format('MMM DD, YYYY')}`}
              </Typography>
            </Stack>
          </Grid>

          {/* View Toggle */}
          <Grid size={{ xs: 12, md: 4 }} display="flex" justifyContent="center">
            <ToggleButtonGroup
              value={view}
              exclusive
              onChange={(e, newView) => newView && handleViewChange(newView)}
              size="small"
            >
              <ToggleButton value="month" aria-label="Month view">
                <Tooltip title="Month">
                  <MonthIcon />
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="agenda" aria-label="Agenda view">
                <Tooltip title="Agenda">
                  <JobIcon />
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
          </Grid>

          {/* Action Buttons */}
          <Grid size={{ xs: 12, md: 4 }} display="flex" justifyContent="flex-end" gap={1}>
            <Tooltip title="Refresh">
              <IconButton onClick={fetchCalendarData} color="primary">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>

        {/* Filters */}
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <ToggleButtonGroup
              value={viewType}
              exclusive
              onChange={(e, newType) => newType && setViewType(newType)}
              size="small"
              fullWidth
            >
              <ToggleButton value="all">All</ToggleButton>
              <ToggleButton value="jobs">Jobs</ToggleButton>
              <ToggleButton value="tasks">Tasks</ToggleButton>
              <ToggleButton value="maintenance">Maintenance</ToggleButton>
            </ToggleButtonGroup>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Truck Filter</InputLabel>
              <Select
                value={selectedTruck}
                label="Truck Filter"
                onChange={(e) => setSelectedTruck(e.target.value)}
              >
                <MenuItem value="">All Trucks</MenuItem>
                {trucks.map((truck) => (
                  <MenuItem key={truck.id} value={truck.id}>
                    {truck.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Employee Filter</InputLabel>
              <Select
                value={selectedEmployee}
                label="Employee Filter"
                onChange={(e) => setSelectedEmployee(e.target.value)}
              >
                <MenuItem value="">All Employees</MenuItem>
                {employees.map((emp) => (
                  <MenuItem key={emp.id} value={emp.id}>
                    {emp.display_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Job Type</InputLabel>
              <Select
                value={selectedJobType}
                label="Job Type"
                onChange={(e) => setSelectedJobType(e.target.value)}
              >
                <MenuItem value="">All Types</MenuItem>
                <MenuItem value="summer">Summer</MenuItem>
                <MenuItem value="winter">Winter</MenuItem>
                <MenuItem value="construction">Construction</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Statistics */}
        {stats && Object.keys(stats).length > 0 && (
          <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Chip 
              label={`Active: ${stats.active_jobs || 0}`} 
              color="primary" 
              size="small" 
            />
            <Chip 
              label={`Completed: ${stats.completed_jobs || 0}`} 
              color="success" 
              size="small" 
            />
            <Chip 
              label={`Trucks: ${stats.trucks_in_use || 0}`} 
              color="default" 
              size="small" 
              icon={<TruckIcon />}
            />
            <Chip 
              label={`Customers: ${stats.unique_customers || 0}`} 
              color="default" 
              size="small" 
            />
          </Box>
        )}
      </CardContent>
    </Card>
  );

  const renderEventDetailsDialog = () => (
    <Dialog 
      open={eventDetailsOpen} 
      onClose={() => setEventDetailsOpen(false)}
      maxWidth="md"
      fullWidth
    >
      {selectedEvent && (
        <>
          <DialogTitle>
            <Stack direction="row" spacing={1} alignItems="center">
              {selectedEvent.type === 'job' && <JobIcon />}
              {selectedEvent.type === 'task' && <JobIcon />}
              {selectedEvent.type === 'maintenance' && <TruckIcon />}
              <Typography variant="h6">{selectedEvent.title}</Typography>
              {selectedEvent.resource?.is_complete && (
                <Chip label="Completed" color="success" size="small" icon={<CheckIcon />} />
              )}
            </Stack>
          </DialogTitle>
          <DialogContent dividers>
            {selectedEvent.type === 'job' && (
              <Grid container spacing={2}>
                <Grid size={12}>
                  <Typography variant="subtitle2" color="text.secondary">Job Number</Typography>
                  <Typography variant="body1">{selectedEvent.resource.job_number}</Typography>
                </Grid>
                <Grid size={12}>
                  <Typography variant="subtitle2" color="text.secondary">Customer</Typography>
                  <Typography variant="body1">{selectedEvent.resource.customer.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    <LocationIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                    {selectedEvent.resource.customer.address}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">Job Type</Typography>
                  <Chip 
                    label={selectedEvent.resource.job_type} 
                    size="small" 
                    color={
                      selectedEvent.resource.job_type === 'summer' ? 'success' :
                      selectedEvent.resource.job_type === 'winter' ? 'info' : 'default'
                    }
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">Scheduled</Typography>
                  <Typography variant="body1">
                    <TimeIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                    {moment(selectedEvent.start).format('MMM DD, YYYY h:mm A')}
                  </Typography>
                </Grid>
                {selectedEvent.resource.truck?.name && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="subtitle2" color="text.secondary">Truck</Typography>
                    <Chip
                      label={selectedEvent.resource.truck.name}
                      size="small"
                      icon={<TruckIcon />}
                      sx={{ 
                        backgroundColor: selectedEvent.resource.truck.color + '33',
                        borderLeft: `3px solid ${selectedEvent.resource.truck.color}`
                      }}
                    />
                  </Grid>
                )}
                {selectedEvent.resource.employees && selectedEvent.resource.employees.length > 0 && (
                  <Grid size={12}>
                    <Typography variant="subtitle2" color="text.secondary">Crew</Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      {selectedEvent.resource.employees.map((emp) => (
                        <Chip
                          key={emp.employee_id}
                          label={emp.employee_name}
                          size="small"
                          icon={<PersonIcon />}
                        />
                      ))}
                    </Stack>
                  </Grid>
                )}
                {selectedEvent.resource.description && (
                  <Grid size={12}>
                    <Typography variant="subtitle2" color="text.secondary">Description</Typography>
                    <Typography variant="body2">{selectedEvent.resource.description}</Typography>
                  </Grid>
                )}
              </Grid>
            )}
            
            {selectedEvent.type === 'task' && (
              <Grid container spacing={2}>
                <Grid size={12}>
                  <Typography variant="subtitle2" color="text.secondary">Description</Typography>
                  <Typography variant="body1">{selectedEvent.resource.description}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">Priority</Typography>
                  <Chip 
                    label={selectedEvent.resource.priority} 
                    size="small"
                    color={
                      selectedEvent.resource.priority === 'high' ? 'error' :
                      selectedEvent.resource.priority === 'medium' ? 'warning' : 'default'
                    }
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">Assigned To</Typography>
                  <Typography variant="body1">{selectedEvent.resource.assigned_to || 'Unassigned'}</Typography>
                </Grid>
                <Grid size={12}>
                  <Typography variant="subtitle2" color="text.secondary">Customer</Typography>
                  <Typography variant="body1">{selectedEvent.resource.customer || 'N/A'}</Typography>
                </Grid>
              </Grid>
            )}

            {selectedEvent.type === 'maintenance' && (
              <Grid container spacing={2}>
                <Grid size={12}>
                  <Typography variant="subtitle2" color="text.secondary">Vehicle</Typography>
                  <Typography variant="body1">{selectedEvent.resource.truck_name}</Typography>
                </Grid>
                <Grid size={12}>
                  <Typography variant="subtitle2" color="text.secondary">Maintenance Type</Typography>
                  <Typography variant="body1">{selectedEvent.resource.maintenance_type}</Typography>
                </Grid>
                {selectedEvent.resource.notes && (
                  <Grid size={12}>
                    <Typography variant="subtitle2" color="text.secondary">Notes</Typography>
                    <Typography variant="body2">{selectedEvent.resource.notes}</Typography>
                  </Grid>
                )}
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEventDetailsOpen(false)}>
              Close
            </Button>
            {selectedEvent.type === 'job' && (
              <Button 
                variant="contained" 
                onClick={() => handleNavigateToJob(selectedEvent.resource.job_id)}
              >
                View Full Details
              </Button>
            )}
          </DialogActions>
        </>
      )}
    </Dialog>
  );

  const renderDayDetailsDialog = () => {
    if (!selectedDayData) {
      return (
        <Dialog open={dayDetailsOpen} onClose={() => setDayDetailsOpen(false)} maxWidth="lg" fullWidth>
          <DialogContent>
            <CircularProgress />
          </DialogContent>
        </Dialog>
      );
    }

    const events = selectedDayData.events || [];
    // Group job/recurring events by truck. Tasks go in their own bucket at
    // the bottom.
    const jobEvents = events.filter((e) => e.event_type === 'job' || e.event_type === 'recurring_job');
    const taskEvents = events.filter((e) => e.event_type === 'task');

    const byTruck = new Map();
    for (const ev of jobEvents) {
      const key = ev.truck_id ? String(ev.truck_id) : 'unassigned';
      if (!byTruck.has(key)) {
        byTruck.set(key, {
          truck_id: ev.truck_id || null,
          truck_name: ev.truck_name || 'Unassigned',
          truck_color: ev.truck_color,
          stops: [],
        });
      }
      byTruck.get(key).stops.push(ev);
    }
    for (const group of byTruck.values()) {
      group.stops.sort((a, b) => {
        const pa = a.priority != null ? Number(a.priority) : 9999;
        const pb = b.priority != null ? Number(b.priority) : 9999;
        return pa - pb;
      });
    }
    const truckGroups = Array.from(byTruck.values()).sort((a, b) =>
      (a.truck_name || '').localeCompare(b.truck_name || '')
    );

    return (
      <Dialog
        open={dayDetailsOpen}
        onClose={() => setDayDetailsOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Schedule for {moment(selectedDayData.date).format('MMMM DD, YYYY')}
          <Typography variant="body2" color="text.secondary">
            {jobEvents.length} stop(s) across {truckGroups.length} truck(s)
            {taskEvents.length > 0 && ` · ${taskEvents.length} task(s)`}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {events.length === 0 && (
            <Alert severity="info">No events scheduled for this day</Alert>
          )}

          {truckGroups.map((group, gi) => (
            <Box key={group.truck_id || 'unassigned'} sx={{ mb: gi === truckGroups.length - 1 ? 0 : 3 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <TruckIcon sx={{ color: group.truck_color || 'primary.main' }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {group.truck_name}
                </Typography>
                <Chip
                  size="small"
                  label={`${group.stops.length} stop${group.stops.length === 1 ? '' : 's'}`}
                  variant="outlined"
                />
              </Stack>
              <List dense disablePadding>
                {group.stops.map((stop, si) => {
                  const isPlanned = stop.is_planned === true;
                  const isCompleted = stop.is_complete === true;
                  const customerName = stop.company_name
                    || `${stop.first_name || ''} ${stop.last_name || ''}`.trim();
                  const address = [stop.street_address, stop.city, stop.province]
                    .filter(Boolean).join(', ');
                  return (
                    <React.Fragment key={`${stop.event_id || stop.job_site_id || si}-${si}`}>
                      {si > 0 && <Divider component="li" />}
                      <ListItem
                        alignItems="flex-start"
                        secondaryAction={
                          stop.event_type === 'job' && stop.event_id ? (
                            <Button
                              size="small"
                              onClick={() => handleNavigateToJob(stop.event_id)}
                            >
                              Open job
                            </Button>
                          ) : null
                        }
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          {isCompleted ? (
                            <CheckIcon color="success" />
                          ) : (
                            <JobIcon color={isPlanned ? 'action' : 'primary'} />
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {stop.priority != null && (
                                  <Box
                                    component="span"
                                    sx={{
                                      display: 'inline-block',
                                      mr: 1,
                                      px: 1,
                                      py: 0.25,
                                      borderRadius: 1,
                                      bgcolor: 'action.selected',
                                      fontSize: '0.75rem',
                                    }}
                                  >
                                    #{stop.priority}
                                  </Box>
                                )}
                                {customerName || stop.title || '—'}
                              </Typography>
                              {stop.job_type && (
                                <Chip size="small" label={stop.job_type} />
                              )}
                              {isPlanned && (
                                <Chip size="small" label="Planned" color="default" variant="outlined" />
                              )}
                              {isCompleted && (
                                <Chip size="small" label="Completed" color="success" />
                              )}
                            </Stack>
                          }
                          secondary={
                            <Box sx={{ mt: 0.5 }}>
                              {address && (
                                <Typography variant="body2" color="text.secondary">
                                  <LocationIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                                  {address}
                                </Typography>
                              )}
                              {showCustomerPhone && stop.phone_number && (
                                <Typography variant="body2" color="text.secondary">
                                  <PhoneIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                                  {stop.phone_number}
                                </Typography>
                              )}
                              {stop.clock_in_time && (
                                <Typography variant="body2" color="text.secondary">
                                  <TimeIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                                  {moment(stop.clock_in_time).format('h:mm A')}
                                  {stop.clock_out_time && ` – ${moment(stop.clock_out_time).format('h:mm A')}`}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                    </React.Fragment>
                  );
                })}
              </List>
            </Box>
          ))}

          {taskEvents.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Tasks
              </Typography>
              <List dense disablePadding>
                {taskEvents.map((task, ti) => (
                  <React.Fragment key={task.event_id || ti}>
                    {ti > 0 && <Divider component="li" />}
                    <ListItem alignItems="flex-start">
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <TaskIcon color="secondary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="body1">{task.title}</Typography>
                            {task.priority && (
                              <Chip
                                label={task.priority}
                                size="small"
                                color={task.priority === 'High' || task.priority === 'Urgent' ? 'error' : 'default'}
                              />
                            )}
                          </Stack>
                        }
                        secondary={
                          <Box sx={{ mt: 0.5 }}>
                            {task.description && (
                              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                {task.description}
                              </Typography>
                            )}
                            {task.assigned_to && (
                              <Typography variant="body2" color="text.secondary">
                                <PersonIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                                {task.assigned_to}
                              </Typography>
                            )}
                            {task.start_time && (
                              <Typography variant="body2" color="text.secondary">
                                <TimeIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                                {moment(task.start_time).format('h:mm A')}
                                {task.end_time && ` – ${moment(task.end_time).format('h:mm A')}`}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDayDetailsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  };

  // ====================================
  // MAIN RENDER
  // ====================================

  if (loading && events.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box data-demo-target="page-calendar-root">
      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {renderToolbar()}

      {/* TKT-237: use a fixed, comfortable height (responsive) instead of a
          viewport-relative height with overflow:hidden. The old
          calc(100vh - 320px) assumed a fixed amount of chrome above; when the
          toolbar/filters wrapped taller (iPad portrait, 1366x768) the last
          week of the month clipped beneath the viewport with no scroll. A
          fixed height that fits all 6 week-rows lets the page scroll to it. */}
      <Paper
        sx={{
          p: 2,
          height: { xs: 600, sm: 680, md: 720 },
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <Calendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            titleAccessor="title"
            view={view}
            date={currentDate}
            onNavigate={handleNavigate}
            onView={handleViewChange}
            onSelectEvent={handleSelectEvent}
            onSelectSlot={handleSelectSlot}
            selectable
            popup
            eventPropGetter={eventStyleGetter}
            views={['month', 'agenda']}
            defaultDate={new Date()}
            style={{ height: '100%' }}
          />
        </Box>
      </Paper>

      {/* TKT-237: legend so users can tell job types / tasks apart by colour. */}
      <Box
        sx={{ mt: 1.5, mb: 1, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}
        aria-label="Calendar colour legend"
      >
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
          Legend:
        </Typography>
        {CALENDAR_LEGEND.map((item) => (
          <Box key={item.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              sx={{
                width: 14,
                height: 14,
                borderRadius: '3px',
                bgcolor: item.color,
                border: '1px solid rgba(0,0,0,0.25)',
                flexShrink: 0,
              }}
            />
            <Typography variant="caption">{item.label}</Typography>
          </Box>
        ))}
      </Box>

      {renderEventDetailsDialog()}
      {renderDayDetailsDialog()}
    </Box>
  );
};

export default CalendarView;
